import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supportAPI } from '../services/api';

export default function SupportNew() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = roll number, 2 = confirmed, fill details
  const [rollNo, setRollNo] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const inputCls = 'w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20';

  async function handleVerify(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await supportAPI.lookup(rollNo.trim());
      setFullName(res.full_name);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Roll number not found. Please check and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await supportAPI.create({ roll_no: rollNo.trim(), full_name: fullName, phone, email, message });
      navigate(`/support/t/${res.token}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || Object.values(err.response?.data?.errors || {})[0] || 'Failed to send. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(145deg,#1b2857_0%,#4a1a06_45%,#f07415_100%)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-white">Contact AWTF Support</h1>
          <p className="mt-2 text-sm text-orange-100/80">Having trouble logging in or using the portal? Tell us what's wrong and we'll get back to you.</p>
        </div>

        <div className="overflow-hidden rounded-[32px] border border-white/12 bg-white p-6 shadow-[0_40px_80px_rgba(0,0,0,0.4)]">
          {step === 1 ? (
            <form onSubmit={handleVerify} className="space-y-4">
              {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Roll Number</label>
                <input value={rollNo} onChange={(e) => setRollNo(e.target.value)} className={inputCls} placeholder="Your AWTF roll number" autoFocus required />
              </div>
              <button type="submit" disabled={busy}
                className="w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {busy ? 'Checking…' : 'Continue'}
              </button>
              <Link to="/login" className="block text-center text-xs font-semibold text-slate-500 hover:underline">Back to Login</Link>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">✓</span>
                <div className="min-w-0">
                  <p className="truncate font-bold text-slate-800">{fullName}</p>
                  <p className="text-xs font-medium text-emerald-700">Roll No. {rollNo}</p>
                </div>
              </div>
              <button type="button" onClick={() => { setStep(1); setError(''); }} className="text-xs font-semibold text-slate-500 hover:underline">Not you? Change roll number</button>

              {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Phone</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="0712 345 678" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@email.com" />
                </div>
              </div>
              <p className="-mt-2 text-xs text-slate-400">At least one of phone or email is needed so we can reply.</p>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">What's the issue?</label>
                <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className={inputCls} placeholder="Describe what's happening…" required />
              </div>

              <button type="submit" disabled={busy}
                className="w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {busy ? 'Sending…' : 'Send to AWTF Support'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
